import React, { useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";

const WelcomeModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if user has seen the welcome modal before
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");
    if (!hasSeenWelcome) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("hasSeenWelcome", "true");
  };

  const content = {
    en: {
      title: "Welcome to Mini Adventurers",
      subtitle:
        "A place where curiosity is encouraged, wonder is central, and every child has the space to grow at their own pace.",
      message: [
        "With us, every child goes on a journey of discovery every day. In a loving and safe environment, children are given the space to play, learn, and be themselves. Together we discover new things, make friends, and grow step by step.",

        "Mini Adventurers is here for all ages: daycare (0-4 years) and after-school care (4-13 years). This way, children can come to us for many years of fun, development, and security.",

        "Through play, children at Mini Adventurers develop their talents—from imagination and creativity to social and motor skills. We provide structure and warmth, but also enough freedom to experience adventures and dream big.",

        "Our team is ready every day with joy and dedication. Because we believe that a good start lays the best foundation for the future.",

        "At Mini Adventurers, every child is welcome to discover, learn, and above all, enjoy!",

        "Kind regards,",
        "The Mini Adventurers team",
      ],
      closeButton: "Get Started",
    },
    nl: {
      title: "Welkom bij Mini Avonturiers",
      subtitle:
        "Een plek waar nieuwsgierigheid wordt aangemoedigd, verwondering centraal staat, en elk kind de ruimte heeft om in zijn eigen tempo te groeien.",
      message: [
        "Bij ons gaat ieder kind elke dag op ontdekkingsreis. In een liefdevolle en veilige omgeving krijgen kinderen de ruimte om tespelen, te leren enzichzelf te zijn. Samen ontdekken we nieuwe dingen, maken we vriendenen groeien we stap voor stap.",

        "Mini Avonturiers is er voor alle leeftijden: kinderdagverblijf (0-4 jaar) én buitenschoolse opvang (4-13 jaar). Zo kunnen kinderen bij ons jarenlang terecht voor plezier, ontwikkeling en geborgenheid.",

        "Spelenderwijs ontwikkelen kinderen bij Mini Avonturiers hun talenten, van fantasie en creativiteit tot sociale en motorische vaardigheden. Wij bieden structuur en warmte, maar ook genoeg vrijheid om avonturen te beleven en groot te dromen.",

        "Ons team staat elke dag met plezier en toewijding klaar. Want wij geloven dat een fijne start de mooiste basis legt voor de toekomst. ",

        "Bij Mini Avonturiers is ieder kind welkom om te ontdekken, te leren envooral te genieten!",

        "Met vriendelijke groet,",
        "Het Mini Avonturiers team",
      ],
      closeButton: "Aan de slag",
    },
    ar: {
      title: "مرحباً بكم في المغامرون الصغار",
      subtitle:
        "مكان يتم فيه تشجيع الفضول، ويكون الدهشة محورياً، وكل طفل لديه المساحة للنمو بوتيرته الخاصة.",
      message: [
        "عندنا يذهب كل طفل في رحلة استكشاف يومية. في بيئة مليئة بالحب والأمان، يحصل الأطفال على المساحة للعب والتعلم وأن يكونوا على طبيعتهم. معًا نكتشف أشياء جديدة، نكوّن صداقات، وننمو خطوة بخطوة.",

        "ميني أفونتوريرز متاح لجميع الأعمار: حضانة (من 0 إلى 4 سنوات) ورعاية بعد المدرسة (من 4 إلى 13 سنة). وبهذا يمكن للأطفال الانضمام إلينا لسنوات عديدة مليئة بالمرح والتطور والشعور بالأمان.",

        "من خلال اللعب، يطوّر الأطفال في ميني أفونتوريرز مواهبهم، من الخيال والإبداع إلى المهارات الاجتماعية والحركية. نحن نوفر لهم هيكلًا دافئًا، وأيضًا حرية كافية لخوض المغامرات والحلم الكبير.",

        "فريقنا جاهز يوميًا بكل سعادة وإخلاص. لأننا نؤمن أن البداية الجيدة تضع أجمل أساس للمستقبل.",

        "في ميني أفونتوريرز، كل طفل مرحب به ليستكشف، ويتعلم، وقبل كل شيء يستمتع!",

        "مع أطيب التحيات،",
        "فريق ميني أفونتوريرز",
      ],
      closeButton: "ابدأ",
    },
  };

  const currentContent = content[language as keyof typeof content];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#FBEACD] rounded-lg max-w-2xl w-full max-h-[78vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="bg-[#F9DCAC] text-black p-6 rounded-t-lg">
          <h1 className="text-2xl font-bold text-center mb-2">
            {currentContent.title}
          </h1>
          <p className="text-sm text-center opacity-90">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-3 text-neutral-800">
            {currentContent.message.map((paragraph, index) => (
              <p key={index} className="text-sm leading-normal">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Close Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleClose}
              className="bg-[#F9DCAC] text-[#492A19] px-6 py-2 rounded-md text-sm font-medium hover:bg-[#492A19] hover:text-[#F9DCAC] transition-colors border border-[#492A19]"
            >
              {currentContent.closeButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
